import React, { useState, useEffect } from 'react';

import { Button, Form, Divider, Grid, Message } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';

import { flagBusinessSaved } from 'actions/business';
import ConfirmModal from 'components/common/ConfirmModal';
import useProviderServiceSettings from 'components/modules/integrations/hooks/use-provider-service-settings';
import LocationSelect from 'components/pages/ecommerce/common/LocationSelect';
import ErrorHandler from 'libs/errors';
import { multiCaseGet } from 'libs/format';
import { selectBusinessLocations } from 'selectors';

import { SETTINGS_DESCRIPTION } from './ResponseSettingsForm.constants';
import { getInitialSettings, getServiceDataPayload } from './ResponseSettingsForm.utils';
import { filterGoogleConfiguredLocations } from '../../../../pages/BusinessProfilePage/components/BusinessInfoTab/BusinessInfoTab.utils';
import { FIVE_STAR_RATING, STAR_RATINGS } from '../../constants/ratings';
import { getGoogleLocationProviderSettings } from '../../services/provider-service';
import RatingMenu from '../RatingMenu';
import StarRatingSettings from '../StarRatingSettings';

import './ResponseSettingsForm.scss';

export default function ResponseSettingsForm() {
  const dispatch = useDispatch();

  const locations = useSelector(selectBusinessLocations);
  const { providerServiceSettings, updateSetting } = useProviderServiceSettings();
  const locationSettings = getGoogleLocationProviderSettings(providerServiceSettings);
  const [selectedLocationId, setSelectedLocationId] = useState(_get(locations, '0.id', null));
  const [responseSettings, setResponseSettings] = useState(() =>
    getInitialSettings(providerServiceSettings, selectedLocationId),
  );

  const locationSetting = locationSettings.find(
    (setting) => _get(setting, 'serviceData.fishermanLocationId') === selectedLocationId,
  );
  const hasResponseSettings = !_isEmpty(locationSetting);

  const [isSaving, setIsSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [activeRating, setActiveMenu] = useState(FIVE_STAR_RATING);
  const [showSaveAllConfirm, setShowSaveAllConfirm] = useState(false);

  useEffect(() => {
    const newSettings = getInitialSettings(providerServiceSettings, selectedLocationId);
    setResponseSettings(newSettings);
    setTouched(false);
  }, [selectedLocationId]);

  function onStarRatingSettingsChange(rating, field, value) {
    setResponseSettings((prev) => ({
      ...prev,
      ratings: {
        ...prev.ratings,
        [rating]: {
          ...prev.ratings[rating],
          [field]: value,
        },
      },
    }));
    setTouched(true);
  }

  async function onSave(saveToAllLocations = false) {
    try {
      setIsSaving(true);

      if (saveToAllLocations) {
        await Promise.all(
          locationSettings.map(async (setting) => {
            const payload = getServiceDataPayload(setting, responseSettings);
            await updateSetting(setting.id, payload);
          }),
        );
      } else {
        const payload = getServiceDataPayload(locationSetting, responseSettings);
        await updateSetting(locationSetting.id, payload);
      }

      setTouched(false);
      dispatch(flagBusinessSaved(true));
      if (saveToAllLocations) {
        setShowSaveAllConfirm(false);
      }
    } catch (error) {
      ErrorHandler.capture(error);
    } finally {
      setIsSaving(false);
    }
  }

  async function onSaveToAllLocations() {
    await onSave(true);
  }

  function onSaveAllClick() {
    setShowSaveAllConfirm(true);
  }

  function onChangeLocation(newLocation) {
    const newLocationId = _get(newLocation, 'id');
    setSelectedLocationId(newLocationId);
  }

  function filterLocations(location) {
    return filterGoogleConfiguredLocations(location, locationSettings);
  }

  const activeRatingSettings = multiCaseGet(responseSettings, `ratings.${activeRating}`, {});
  const hasMultipleLocations = locationSettings.length > 1;

  return (
    <div className="business-profile-response-settings">
      {!hasResponseSettings && (
        <p className="settings-description">
          To configure response settings, you must first complete your business profile in the
          &quot;Connection&quot; tab.
        </p>
      )}
      {hasResponseSettings && (
        <>
          <Message content={SETTINGS_DESCRIPTION} />
          <div className="controls">
            <LocationSelect
              activeLocation={selectedLocationId}
              onChangeLocation={onChangeLocation}
              global={false}
              fluid
              hideAllLocationsOption
              filterLocations={filterLocations}
              hideIfSingleLocation
            />
          </div>
          <RatingMenu
            starRatings={STAR_RATINGS}
            activeRating={activeRating}
            setActiveRating={setActiveMenu}
          />
          <Form>
            <StarRatingSettings
              rating={activeRating}
              value={activeRatingSettings}
              onChange={(field, value) => onStarRatingSettingsChange(activeRating, field, value)}
            />
            <Divider />
            <div className="buttons">
              <Grid columns={2} stackable>
                <Grid.Column>
                  <Button
                    primary
                    fluid
                    size="large"
                    onClick={() => onSave(false)}
                    loading={isSaving}
                    disabled={isSaving || !touched}
                    content={isSaving ? 'Saving...' : 'Save Settings'}
                  />
                </Grid.Column>
                {hasMultipleLocations && (
                  <Grid.Column>
                    <Button
                      primary
                      fluid
                      size="large"
                      onClick={onSaveAllClick}
                      loading={isSaving}
                      disabled={isSaving || !touched}
                      content={
                        isSaving ? 'Saving to All Locations...' : 'Save Settings to All Locations'
                      }
                    />
                  </Grid.Column>
                )}
              </Grid>
            </div>
          </Form>
          <ConfirmModal
            open={showSaveAllConfirm}
            header="Save Settings to All Locations"
            content="This will save current settings to all locations. Are you sure you want to continue?"
            onCancel={() => setShowSaveAllConfirm(false)}
            onConfirm={onSaveToAllLocations}
            confirmButton="Save to All"
            cancelButton="Cancel"
          />
        </>
      )}
    </div>
  );
}
